// Shared Meilisearch-querying core used by SearchPopup.js (header overlay) and
// SearchResults.js (/suche inline page). Both are Alpine components with the
// same request/response pipeline and identical UI-state getters; only the
// lifecycle around them differs (modal vs URL-synced inline). Keeping the
// search engine in one place prevents the drift we saw before - every fix
// (UTC dates, singular/plural, no-results consolidation) had to land in two
// copies otherwise.

export const MIN_CHARS = 3;
export const DEBOUNCE_MS = 250;
export const HITS_PER_PAGE = 20;

// Bound the boot-time MeiliSearch client lookup and _runSearch's wait-for-client
// retries so the page never enters a silent setTimeout loop if the UMD fails
// to load.
export const CLIENT_BOOT_MAX_RETRIES = 20; // 20 * 50 ms = 1 s
export const RUN_SEARCH_MAX_WAIT_MS = 3000;

const GERMAN_MONTHS = [
    'JANUAR', 'FEBRUAR', 'MÄRZ', 'APRIL', 'MAI', 'JUNI',
    'JULI', 'AUGUST', 'SEPTEMBER', 'OKTOBER', 'NOVEMBER', 'DEZEMBER',
];

// The index stores publication dates as UTC-normalized ATOM strings (see
// CodeQ\Search\Eel\NodeSearchHelper::toUtcAtom). Reading them with UTC getters
// keeps the rendered day identical across client timezones, matching what the
// editor saw on publish.
const formatGermanDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getUTCDate()}. ${GERMAN_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    })[char]);

const MARK_OPEN = '___SEARCH_MARK_OPEN___';
const MARK_CLOSE = '___SEARCH_MARK_CLOSE___';

const NAMED_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    shy: '',
};

// Inline editing stores titles and rich text HTML-encoded, and the index keeps that
// verbatim. Decoding is safe because every caller escapes afterwards; it is done
// without the DOM so no markup can be reintroduced here.
const decodeEntities = (value) =>
    String(value || '').replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, reference) => {
        if (reference[0] !== '#') {
            const named = NAMED_ENTITIES[reference.toLowerCase()];
            return named === undefined ? entity : named;
        }

        const codePoint = reference[1] === 'x' || reference[1] === 'X'
            ? parseInt(reference.slice(2), 16)
            : parseInt(reference.slice(1), 10);

        if (!Number.isFinite(codePoint) || codePoint < 32 || codePoint > 0x10ffff) {
            return entity;
        }

        try {
            return String.fromCodePoint(codePoint);
        } catch (error) {
            return entity;
        }
    });

const normalizeText = (value, preserveMarks = false) => {
    let text = String(value || '');

    if (preserveMarks) {
        text = text.replace(/<mark>/gi, MARK_OPEN).replace(/<\/mark>/gi, MARK_CLOSE);
    }

    text = decodeEntities(
        text
            .replace(/<\/?(br|p|div|li|h[1-6])\b[^>]*>/gi, ' ')
            // Inline tags join what they split: inline editing leaves words like
            // "Öster</span><span>reich" in stored titles.
            .replace(/<\/?[a-z][^>]*>/gi, '')
            // A snippet crop can end inside a tag, which a browser drops, or inside an
            // entity such as "&nbsp…", which a browser still resolves.
            .replace(/<\/?[a-z][^>]*$/i, '')
            .replace(/&(#x?[0-9a-f]+|amp|lt|gt|quot|apos|nbsp|shy)(?=[^a-z0-9;]*$)/i, '&$1;')
    )
        .replace(/\s+/g, ' ')
        .trim();

    if (preserveMarks) {
        text = text
            .replace(new RegExp(MARK_OPEN, 'g'), '<mark>')
            .replace(new RegExp(MARK_CLOSE, 'g'), '</mark>');
    }

    return text;
};

// Title and snippet are bound with x-html, which also initialises Alpine directives in
// whatever it inserts: only the <mark> highlighting may survive as markup.
const allowMarkTags = (value) =>
    escapeHtml(normalizeText(value, true))
        .replace(/&lt;mark&gt;/g, '<mark>')
        .replace(/&lt;\/mark&gt;/g, '</mark>');

const stripTags = (value) => normalizeText(value);

// Produce the display fields the hit template expects, so the Fusion stays
// simple. We pick the best snippet ourselves rather than relying on
// Meilisearch's single `attributesToCrop` winner, so we can skip snippets that
// merely echo the title.
export const decorateHit = (hit) => {
    const titleText = stripTags(hit.title);
    let snippetHtml = '';
    const formattedFulltext = hit._formatted?.__fulltext || {};
    for (const field of ['text', 'h2', 'h1']) {
        const value = formattedFulltext[field];
        if (!value || !value.includes('<mark>')) continue;
        if (stripTags(value) === titleText) continue;
        snippetHtml = allowMarkTags(value);
        break;
    }
    return {
        ...hit,
        __titleHtml: allowMarkTags(hit._formatted?.title || hit.title || ''),
        __breadcrumbText: Array.isArray(hit.__breadcrumb)
            ? hit.__breadcrumb.filter(Boolean).map(stripTags).join(' > ')
            : '',
        __dateLabel: formatGermanDate(hit.__publicationDate),
        __snippetHtml: snippetHtml,
    };
};

const buildSiteFilter = (siteNodePath) =>
    `(__parentPath = "${siteNodePath}" OR __path = "${siteNodePath}")`;

/**
 * Build the shared Alpine data object for a Meilisearch-backed search widget.
 *
 * Returns an object that callers mutate in place to add their mode-specific
 * fields (showSearch/topOffset/openModal for the popup, updateUrl/loadMore
 * for the inline page). Mutating in place - rather than spreading - preserves
 * the `visibleSummary` / `announcement` / `hasMore` getters, which would be
 * invoked-and-flattened to plain string properties by `{...core}`.
 *
 * The Meilisearch client is stored in closure scope, not as reactive state:
 * meilisearch-js uses private class methods (`#foo`) that throw "Cannot
 * access private method" when the instance is accessed through Alpine's
 * reactive Proxy.
 */
export const createSearchCore = ({
    host = '',
    apiKey = '',
    indexName = '',
    siteNodePath = '',
    labels = {},
    logPrefix = 'Search',
    onShortQuery = null,
    onSearchSuccess = null,
} = {}) => {
    let client = null;
    let index = null;
    let debounceTimer = null;
    let bootstrapFailed = false;

    const ensureClient = (retries = CLIENT_BOOT_MAX_RETRIES) => {
        if (client || bootstrapFailed) return;
        if (!window.meilisearch?.MeiliSearch) {
            if (retries > 0) {
                setTimeout(() => ensureClient(retries - 1), 50);
            } else {
                bootstrapFailed = true;
                console.error(`[${logPrefix}] meilisearch UMD never appeared on window; giving up`);
            }
            return;
        }
        client = new window.meilisearch.MeiliSearch({ host, apiKey });
        index = client.index(indexName);
    };

    const data = {
        labels,
        query: '',
        hits: [],
        resultCount: null,
        loading: false,
        error: null,
        page: 0,

        ensureClient,
        clearDebounce() {
            clearTimeout(debounceTimer);
        },

        scheduleSearch() {
            clearTimeout(debounceTimer);
            const q = (this.query || '').trim();
            if (q.length < MIN_CHARS) {
                this.hits = [];
                this.resultCount = null;
                this.error = null;
                this.page = 0;
                if (onShortQuery) onShortQuery();
                return;
            }
            debounceTimer = setTimeout(() => this._runSearch(q, 0), DEBOUNCE_MS);
        },

        runSearchNow() {
            clearTimeout(debounceTimer);
            const q = (this.query || '').trim();
            if (q.length < MIN_CHARS) return;
            this._runSearch(q, 0);
        },

        async _runSearch(q, page = 0, waitedMs = 0) {
            ensureClient();
            if (bootstrapFailed) {
                this.error = this.labels.errorLabel || 'Search failed.';
                this.loading = false;
                return;
            }
            if (!index) {
                if (waitedMs >= RUN_SEARCH_MAX_WAIT_MS) {
                    bootstrapFailed = true;
                    this.error = this.labels.errorLabel || 'Search failed.';
                    this.loading = false;
                    console.error(`[${logPrefix}] MeiliSearch client never ready; aborting _runSearch`);
                    return;
                }
                setTimeout(() => this._runSearch(q, page, waitedMs + 100), 100);
                return;
            }
            this.loading = true;
            this.error = null;
            try {
                const response = await index.search(q, {
                    filter: buildSiteFilter(siteNodePath),
                    // Highlight the fulltext fields too, not just the title:
                    // decorateHit below only picks a snippet when the cropped
                    // value actually contains <mark> tags (so we skip snippets
                    // that merely echo the title). Without highlighting the
                    // fulltext fields, _formatted.__fulltext.* comes back
                    // without <mark> and the snippet row silently never renders.
                    attributesToHighlight: ['title', '__fulltext.text', '__fulltext.h1', '__fulltext.h2'],
                    attributesToCrop: ['__fulltext.text:30', '__fulltext.h1:15', '__fulltext.h2:15'],
                    highlightPreTag: '<mark>',
                    highlightPostTag: '</mark>',
                    cropMarker: '…',
                    limit: HITS_PER_PAGE,
                    offset: page * HITS_PER_PAGE,
                });
                if ((this.query || '').trim() !== q) return;
                // A document whose URI could not be resolved at index time would render as a result linking nowhere.
                const newHits = (response.hits || []).filter((hit) => hit.__uri).map(decorateHit);
                this.hits = page === 0 ? newHits : [...this.hits, ...newHits];
                this.resultCount = response.estimatedTotalHits ?? response.hits.length;
                this.page = page;
                if (onSearchSuccess) onSearchSuccess(q, page);
            } catch (e) {
                console.error(`[${logPrefix}] search failed`, e);
                this.error = this.labels.errorLabel || 'Search failed.';
                if (page === 0) {
                    this.hits = [];
                    this.resultCount = null;
                }
            } finally {
                this.loading = false;
            }
        },
    };

    // Getters defined via defineProperty so that in-place mutation of `data`
    // by the caller (eg. `data.showSearch = false`) does not strip them the
    // way an object spread would.
    Object.defineProperty(data, 'hasMore', {
        enumerable: true,
        configurable: true,
        get() {
            // Counted in fetched pages, not rendered hits: with unlinkable hits dropped,
            // hits.length never reaches resultCount and this would stay true past the last page.
            return this.resultCount !== null && (this.page + 1) * HITS_PER_PAGE < this.resultCount;
        },
    });

    // Single status line for every non-loading, non-error state:
    // - zero hits   -> noSearchResultsLabel
    // - exactly one -> singular ("1 Suchergebnis …")
    // - otherwise   -> plural
    // Falls back to English placeholders if labels are missing
    // (e.g. styleguide preview).
    Object.defineProperty(data, 'visibleSummary', {
        enumerable: true,
        configurable: true,
        get() {
            let template;
            if (this.resultCount === 0) {
                template = this.labels.noSearchResultsLabel || 'No results found for „{query}".';
            } else if (this.resultCount === 1) {
                template = this.labels.resultSummarySingularLabel || '{count} result for „{query}"';
            } else {
                template = this.labels.resultSummaryLabel || '{count} results for „{query}"';
            }
            return template
                .replace('{count}', this.resultCount)
                .replace('{query}', this.query.trim());
        },
    });

    // Feeds the sr-only polite live region; only changes on count/state
    // transitions so screen readers do not narrate every keystroke.
    Object.defineProperty(data, 'announcement', {
        enumerable: true,
        configurable: true,
        get() {
            if (this.loading) return this.labels.loadingLabel || '';
            if (this.error) return this.error;
            if (this.query.trim().length < MIN_CHARS) return '';
            if (this.resultCount !== null) return this.visibleSummary;
            return '';
        },
    });

    return data;
};
